// DiagnosticProgressReport.js — v6
// Bluish theme aligned with the Individual Report.
// #2E7DFD (strong blue) + #8FC6FE (light blue) as primary palette.
// Blue = improvement, Amber = decline.
// Layout: Score Comparison (stacked bar) → Difficulty ×3 → Charts ×2 →
//         Full-Width Skill Breakdown → Subcategory → Takeaways → Focus
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../../../../firebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import DonutChart from "./DonutChart";

// ─── Color tokens ─────────────────────────────────────────────────────────────
const B_STRONG  = "#2E7DFD"; // strong blue  – Math / positive delta / trend
const B_LIGHT   = "#8FC6FE"; // light blue   – R&W segment / secondary
const B_GRAD    = "linear-gradient(90deg, #dbeafe, #e0e7ff)"; // badge gradient
const VIOLET    = "#6d28d9"; // R&W trend line
const POS_BG    = "#dbeafe"; // positive badge bg
const POS_TEXT  = "#1d4ed8"; // positive badge text
const NEG_BG    = "#fef3c7"; // negative badge bg
const NEG_TEXT  = "#d97706"; // negative badge text (amber)
const TILE_BG   = "linear-gradient(145deg, #f8fafc, #eef2ff)"; // Individual Report tile
const CARD_BG   = "#ffffff";
const BORDER    = "#e5e7eb";
const TEXT_PRI  = "#111827";
const TEXT_SEC  = "#64748b";
const TEXT_MUT  = "#9ca3af";

// ─── SAT structure ────────────────────────────────────────────────────────────
const SAT_STRUCTURE = {
  "Reading & Writing": {
    "Information and Ideas":       ["Central Ideas and Details", "Inferences", "Command of Evidence"],
    "Craft and Structure":         ["Words in Context", "Text Structure and Purpose", "Cross-Text Connections"],
    "Expression of Ideas":         ["Rhetorical Synthesis", "Transitions"],
    "Standard English Conventions":["Boundaries", "Form, Structure, and Sense"],
  },
  Math: {
    Algebra: [
      "Linear equations in one variable",
      "Linear equations in two variables",
      "Linear functions",
      "Systems of two linear equations in two variables",
      "Linear inequalities in one or two variables",
    ],
    "Advanced Math": [
      "Equivalent expressions",
      "Nonlinear equations in one variable and systems of equations in two variables",
      "Nonlinear functions",
    ],
    "Problem-Solving and Data Analysis": [
      "Ratios, rates, proportional relationships, and units",
      "Percentages",
      "One-variable data - Distributions and measures of center and spread",
      "Two-variable data - Models and scatterplots",
      "Probability and conditional probability",
      "Inference from sample statistics and margin of error",
      "Evaluating statistical claims - Observational studies and experiments",
    ],
    "Geometry and Trigonometry": [
      "Area and volume",
      "Lines, angles, and triangles",
      "Right triangles and trigonometry",
      "Circles",
    ],
  },
};

const ALL_SUBS = Object.entries(SAT_STRUCTURE).flatMap(([subject, units]) =>
  Object.entries(units).flatMap(([unit, subs]) =>
    subs.map((sub) => ({ subject, unit, sub }))
  )
);

const normalize = (str) =>
  String(str || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// ─── Helpers ──────────────────────────────────────────────────────────────────
function isCorrectAnswer(sRaw, cRaw) {
  if (!sRaw || !cRaw) return false;
  const s = String(sRaw).trim().toUpperCase();
  const opts = String(cRaw).split(",").map((x) => x.trim().toUpperCase());
  if (opts.includes(s)) return true;
  const parse = (v) => {
    if (v.includes("/")) { const [n, d] = v.split("/"); return parseFloat(n) / parseFloat(d); }
    return parseFloat(v);
  };
  const sn = parse(s);
  if (isNaN(sn)) return false;
  return opts.some((o) => { const on = parse(o); return !isNaN(on) && Math.abs(sn - on) < 0.01; });
}

function toDateSafe(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") return v.toDate();
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function pct(correct, total) {
  if (!total || total === 0) return null;
  return Math.round((correct / total) * 100);
}

function resolveScore(a) { return a?.totalScore ?? a?.totalScaled ?? a?.totalScaledScore ?? null; }
function resolveRW(a)    { return a?.rwScaledScore ?? a?.rwScaled ?? null; }
function resolveMath(a)  { return a?.mathScaledScore ?? a?.mathScaled ?? null; }

function normDiff(d) {
  const s = String(d || "").trim().toLowerCase();
  if (s === "easy") return "Easy";
  if (s === "medium") return "Medium";
  if (s === "hard" || s === "difficult") return "Hard";
  return null;
}

function computeAttemptStats(attempt, questionIndex) {
  const answers = attempt?.answers || {};
  const overall = { correct: 0, total: 0 };
  const rwStats = { correct: 0, total: 0 };
  const mathStats = { correct: 0, total: 0 };
  const diffBuckets = {
    Easy: { correct: 0, total: 0 },
    Medium: { correct: 0, total: 0 },
    Hard: { correct: 0, total: 0 },
  };
  const subStats = {};

  for (const [qid, ansRaw] of Object.entries(answers)) {
    const meta = questionIndex.get(qid);
    if (!meta) continue;
    const sa = String(ansRaw || "").trim().toUpperCase();
    const skip = !sa;
    const ok = !skip && isCorrectAnswer(sa, meta.correctAnswer);
    overall.total++; if (ok) overall.correct++;
    const mod = String(meta.module || "").toLowerCase();
    const isRW = mod.includes("rw") || mod.includes("english") || mod.includes("reading") || mod.includes("writing");
    const isMath = mod.includes("math");
    if (isRW)       { rwStats.total++;   if (ok) rwStats.correct++;   }
    else if (isMath){ mathStats.total++; if (ok) mathStats.correct++; }
    const diff = normDiff(meta.difficulty);
    if (diff) { diffBuckets[diff].total++; if (ok) diffBuckets[diff].correct++; }
    const subKey = normalize(meta.subcategory || "");
    if (subKey) {
      if (!subStats[subKey]) subStats[subKey] = { correct: 0, total: 0 };
      subStats[subKey].total++; if (ok) subStats[subKey].correct++;
    }
  }

  return {
    overall, rwStats, mathStats, diffBuckets, subStats,
    accuracy: pct(overall.correct, overall.total),
    rwAccuracy: pct(rwStats.correct, rwStats.total),
    mathAccuracy: pct(mathStats.correct, mathStats.total),
  };
}

function computeUnitPct(subs, subStats) {
  const pcts = subs
    .map((sub) => { const k = normalize(sub); const d = subStats?.[k]; return d && d.total > 0 ? pct(d.correct, d.total) : null; })
    .filter((p) => p != null);
  return pcts.length > 0 ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : null;
}

// ─── Delta tag (blue/amber) ───────────────────────────────────────────────────
function DeltaTag({ delta, suffix = "pts" }) {
  if (delta == null) return null;
  const isPos = delta > 0, isZero = delta === 0;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 999,
      background: isZero ? "#f8fafc" : isPos ? POS_BG : NEG_BG,
      color:      isZero ? TEXT_SEC  : isPos ? POS_TEXT : NEG_TEXT,
      border: `1px solid ${isZero ? BORDER : isPos ? "#bfdbfe" : "#fde68a"}`,
      whiteSpace: "nowrap",
    }}>
      {isZero ? "No change" : `${isPos ? "▲ +" : "▼ "}${delta} ${suffix}`}
    </span>
  );
}

// ─── Accuracy badge (matches Individual Report style — blue palette) ──────────
function AccBadge({ percentage }) {
  if (percentage == null) return <span style={{ color: TEXT_MUT }}>—</span>;
  const bg   = percentage >= 80 ? "#dbeafe" : percentage >= 60 ? "#fef9c3" : "#fee2e2";
  const text = percentage >= 80 ? "#1d4ed8" : percentage >= 60 ? "#92400e" : "#b91c1c";
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 999, background: bg, color: text, display: "inline-block" }}>
      {percentage}%
    </span>
  );
}

// ─── Stacked bar comparison (R&W + Math segments) ─────────────────────────────
function StackedBarComparison({ statsA, statsB, labelA, labelB, totalDelta }) {
  const [hovered, setHovered] = useState(null); // "a" | "b"
  const uid = useRef(`sc-${Math.random().toString(36).substr(2, 6)}`).current;

  const W = 350, H = 320;
  const padT = 34, padB = 36, padL = 20, padR = 20;
  const innerH = H - padT - padB;
  const barW = 90, gap = 55;
  const totalBarsW = barW * 2 + gap;
  const barAX = (W - totalBarsW) / 2;
  const barBX = barAX + barW + gap;
  const baseY = H - padB;
  const MAX = 1600;

  const toH = (v) => v != null ? (Math.max(0, Math.min(MAX, v)) / MAX) * innerH : 0;

  const rwA = statsA?.rwScore, mathA = statsA?.mathScore, totalA = statsA?.totalScore;
  const rwB = statsB?.rwScore, mathB = statsB?.mathScore, totalB = statsB?.totalScore;

  const rwAH = toH(rwA), mathAH = toH(mathA), totalAH = rwAH + mathAH;
  const rwBH = toH(rwB), mathBH = toH(mathB), totalBH = rwBH + mathBH;

  // Center of the gap between bars
  const gapCX = barAX + barW + gap / 2;
  const deltaMidY = baseY - (totalAH + totalBH) / 2;

  // Ref lines at 400/800/1200/1600
  const refLines = [400, 800, 1200, 1600];

  return (
    <div style={{ position: "relative", display: "flex", justifyContent: "center" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", maxWidth: W, height: "auto", display: "block", overflow: "visible" }}
      >
        <defs>
          <clipPath id={`${uid}-a`}>
            <rect x={barAX} y={baseY - totalAH} width={barW} height={Math.max(totalAH, 1)} rx={6} />
          </clipPath>
          <clipPath id={`${uid}-b`}>
            <rect x={barBX} y={baseY - totalBH} width={barW} height={Math.max(totalBH, 1)} rx={6} />
          </clipPath>
        </defs>

        {/* Reference grid lines */}
        {refLines.map((v) => {
          const ly = baseY - toH(v);
          return (
            <g key={v}>
              <line x1={padL} x2={W - padR} y1={ly} y2={ly} stroke="#e5e7eb" strokeWidth={1} strokeDasharray="3 2" />
              <text x={padL - 4} y={ly + 3} textAnchor="end" style={{ fontSize: 8, fill: TEXT_MUT }}>{v}</text>
            </g>
          );
        })}

        {/* Baseline */}
        <line x1={padL} x2={W - padR} y1={baseY} y2={baseY} stroke="#d1d5db" strokeWidth={1.5} />

        {/* Bar A */}
        <g style={{ cursor: "pointer" }}
          onMouseEnter={() => setHovered("a")} onMouseLeave={() => setHovered(null)}>
          {totalAH > 0 && (
            <g clipPath={`url(#${uid}-a)`}>
              <rect x={barAX} y={baseY - rwAH}         width={barW} height={rwAH}   fill={B_LIGHT} />
              <rect x={barAX} y={baseY - totalAH}      width={barW} height={mathAH} fill={B_STRONG} />
            </g>
          )}
          {/* White divider between segments */}
          {rwAH > 0 && mathAH > 0 && (
            <line x1={barAX} x2={barAX + barW} y1={baseY - rwAH} y2={baseY - rwAH} stroke="rgba(255,255,255,0.5)" strokeWidth={1.5} />
          )}
          {/* Score labels on segments */}
          {mathAH >= 18 && mathA != null && (
            <text x={barAX + barW / 2} y={baseY - totalAH + mathAH / 2 + 4}
              textAnchor="middle" style={{ fontSize: 11, fontWeight: 700, fill: "#fff" }}>{mathA}</text>
          )}
          {rwAH >= 18 && rwA != null && (
            <text x={barAX + barW / 2} y={baseY - rwAH / 2 + 4}
              textAnchor="middle" style={{ fontSize: 11, fontWeight: 700, fill: "#1e3a8a" }}>{rwA}</text>
          )}
          {/* Total above bar */}
          {totalA != null && (
            <text x={barAX + barW / 2} y={baseY - totalAH - 9}
              textAnchor="middle" style={{ fontSize: 14, fontWeight: 800, fill: TEXT_PRI }}>{totalA}</text>
          )}
          {/* Label below */}
          <text x={barAX + barW / 2} y={baseY + 16}
            textAnchor="middle" style={{ fontSize: 10, fill: TEXT_SEC, fontWeight: 600 }}>{labelA}</text>
        </g>

        {/* Bar B */}
        <g style={{ cursor: "pointer" }}
          onMouseEnter={() => setHovered("b")} onMouseLeave={() => setHovered(null)}>
          {totalBH > 0 && (
            <g clipPath={`url(#${uid}-b)`}>
              <rect x={barBX} y={baseY - rwBH}    width={barW} height={rwBH}   fill={B_LIGHT} />
              <rect x={barBX} y={baseY - totalBH} width={barW} height={mathBH} fill={B_STRONG} />
            </g>
          )}
          {rwBH > 0 && mathBH > 0 && (
            <line x1={barBX} x2={barBX + barW} y1={baseY - rwBH} y2={baseY - rwBH} stroke="rgba(255,255,255,0.5)" strokeWidth={1.5} />
          )}
          {mathBH >= 18 && mathB != null && (
            <text x={barBX + barW / 2} y={baseY - totalBH + mathBH / 2 + 4}
              textAnchor="middle" style={{ fontSize: 11, fontWeight: 700, fill: "#fff" }}>{mathB}</text>
          )}
          {rwBH >= 18 && rwB != null && (
            <text x={barBX + barW / 2} y={baseY - rwBH / 2 + 4}
              textAnchor="middle" style={{ fontSize: 11, fontWeight: 700, fill: "#1e3a8a" }}>{rwB}</text>
          )}
          {totalB != null && (
            <text x={barBX + barW / 2} y={baseY - totalBH - 9}
              textAnchor="middle" style={{ fontSize: 14, fontWeight: 800, fill: TEXT_PRI }}>{totalB}</text>
          )}
          <text x={barBX + barW / 2} y={baseY + 16}
            textAnchor="middle" style={{ fontSize: 10, fill: TEXT_SEC, fontWeight: 600 }}>{labelB}</text>
        </g>

        {/* Delta badge between bars */}
        {totalDelta != null && (
          <>
            <rect x={gapCX - 20} y={deltaMidY - 13} width={40} height={24} rx={10}
              fill={totalDelta >= 0 ? POS_BG : NEG_BG} />
            <text x={gapCX} y={deltaMidY + 3} textAnchor="middle"
              style={{ fontSize: 12, fontWeight: 800, fill: totalDelta >= 0 ? POS_TEXT : NEG_TEXT }}>
              {totalDelta >= 0 ? "+" : ""}{totalDelta}
            </text>
          </>
        )}

        {/* Legend */}
        <rect x={barAX} y={H - 12} width={14} height={12} rx={2} fill={B_STRONG} />
        <text x={barAX + 18} y={H - 3} style={{ fontSize: 9, fill: TEXT_SEC }}>Math</text>
        <rect x={barAX + 52} y={H - 12} width={14} height={12} rx={2} fill={B_LIGHT} />
        <text x={barAX + 70} y={H - 3} style={{ fontSize: 9, fill: TEXT_SEC }}>Reading &amp; Writing</text>
      </svg>

      {/* Hover tooltip */}
      {hovered && (
        <div style={{
          position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
          background: "#0f172a", color: "#f8fafc", borderRadius: 10, padding: "10px 14px",
          fontSize: 12, pointerEvents: "none", zIndex: 20, whiteSpace: "nowrap",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
        }}>
          {(() => {
            const s = hovered === "a" ? statsA : statsB;
            const lbl = hovered === "a" ? labelA : labelB;
            return (
              <>
                <div style={{ fontWeight: 800, marginBottom: 6, borderBottom: "1px solid #334155", paddingBottom: 4 }}>
                  {lbl} · {s?.date}
                </div>
                <div>Total: <strong>{s?.totalScore ?? "—"}</strong></div>
                <div>R&amp;W: <strong style={{ color: B_LIGHT }}>{s?.rwScore ?? "—"}</strong></div>
                <div>Math: <strong style={{ color: "#93c5fd" }}>{s?.mathScore ?? "—"}</strong></div>
                {s?.accuracy != null && <div>Accuracy: <strong>{s.accuracy}%</strong></div>}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ─── Score trend line ─────────────────────────────────────────────────────────
function ScoreTrendChart({ attemptStats }) {
  const [hovIdx, setHovIdx] = useState(null);
  const svgRef = useRef(null);
  const n = attemptStats.length;
  const W = 460, H = 180, padL = 44, padR = 16, padT = 26, padB = 36;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const scores = attemptStats.map((a) => a.totalScore).filter((v) => v != null);
  if (scores.length === 0) return null;
  const rawMin = Math.min(...scores), rawMax = Math.max(...scores);
  const yMin = Math.floor(rawMin / 100) * 100 - 80;
  const yMax = Math.ceil(rawMax / 100) * 100 + 40;
  const yRange = yMax - yMin;
  const xPos = (i) => padL + (i / Math.max(n - 1, 1)) * innerW;
  const yPos = (v) => padT + innerH * (1 - (v - yMin) / yRange);
  const step = yRange <= 300 ? 50 : 100;
  const yTicks = [];
  for (let v = Math.ceil(yMin / step) * step; v <= yMax; v += step) yTicks.push(v);
  const pts = attemptStats.map((a, i) => a.totalScore != null ? `${xPos(i)},${yPos(a.totalScore)}` : null).filter(Boolean).join(" ");
  const onMove = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const relX = ((e.clientX - rect.left) / rect.width) * W;
    let minD = Infinity, minI = 0;
    for (let i = 0; i < n; i++) { const d = Math.abs(xPos(i) - relX); if (d < minD) { minD = d; minI = i; } }
    setHovIdx(minI);
  };
  const firstScore = attemptStats[0]?.totalScore, lastScore = attemptStats[n - 1]?.totalScore;
  const totalImpr = firstScore != null && lastScore != null ? lastScore - firstScore : null;
  const avgGain = totalImpr != null && n > 1 ? (totalImpr / (n - 1)).toFixed(1) : null;

  return (
    <div>
      <div style={{ position: "relative" }}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`}
          style={{ width: "100%", height: "auto", display: "block", cursor: "crosshair" }}
          onMouseMove={onMove} onMouseLeave={() => setHovIdx(null)}>
          {yTicks.map((v) => (
            <g key={v}>
              <line x1={padL} x2={W - padR} y1={yPos(v)} y2={yPos(v)} stroke="#eef2ff" strokeWidth={1} />
              <text x={padL - 6} y={yPos(v) + 4} textAnchor="end" style={{ fontSize: 9, fill: TEXT_MUT }}>{v}</text>
            </g>
          ))}
          {attemptStats.map((_, i) => (
            <text key={i} x={xPos(i)} y={H - 6} textAnchor="middle" style={{ fontSize: 9, fill: TEXT_SEC }}>T{i + 1}</text>
          ))}
          {hovIdx != null && (
            <line x1={xPos(hovIdx)} x2={xPos(hovIdx)} y1={padT} y2={padT + innerH}
              stroke="#bfdbfe" strokeWidth={1.5} strokeDasharray="4 3" />
          )}
          <polyline points={pts} fill="none" stroke={B_STRONG} strokeWidth={2.5} strokeLinejoin="round" />
          {attemptStats.map((a, i) => {
            if (a.totalScore == null) return null;
            const cx = xPos(i), cy = yPos(a.totalScore), isHov = hovIdx === i;
            return (
              <g key={i}>
                {n <= 7 && (
                  <text x={cx} y={i % 2 === 0 ? cy - 10 : cy + 18} textAnchor="middle"
                    style={{ fontSize: 9, fontWeight: 700, fill: B_STRONG }}>{a.totalScore}</text>
                )}
                <circle cx={cx} cy={cy} r={isHov ? 5.5 : 3.5} fill={B_STRONG} stroke="#fff" strokeWidth={isHov ? 2 : 1.5} />
              </g>
            );
          })}
        </svg>
        {hovIdx != null && (
          <div style={{
            position: "absolute", top: 4, left: "50%", transform: "translateX(-50%)",
            background: "#0f172a", color: "#f8fafc", borderRadius: 8, padding: "8px 12px",
            fontSize: 11, pointerEvents: "none", zIndex: 10, whiteSpace: "nowrap",
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          }}>
            <strong>Test {hovIdx + 1}</strong> · {attemptStats[hovIdx]?.date}
            <br />Score: <strong>{attemptStats[hovIdx]?.totalScore ?? "—"}</strong>
          </div>
        )}
      </div>
      {(totalImpr != null || avgGain != null) && (
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          {totalImpr != null && (
            <div style={{ flex: 1, background: totalImpr >= 0 ? POS_BG : NEG_BG, borderRadius: 10, padding: "9px 12px", border: `1px solid ${totalImpr >= 0 ? "#bfdbfe" : "#fde68a"}` }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: TEXT_MUT, marginBottom: 3 }}>Improvement</div>
              <div style={{ fontSize: 17, fontWeight: 900, color: totalImpr >= 0 ? POS_TEXT : NEG_TEXT }}>
                {totalImpr >= 0 ? "+" : ""}{totalImpr} pts
              </div>
            </div>
          )}
          {avgGain != null && n > 2 && (
            <div style={{ flex: 1, background: TILE_BG, borderRadius: 10, padding: "9px 12px", border: `1px solid ${BORDER}` }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: TEXT_MUT, marginBottom: 3 }}>Avg Gain / Test</div>
              <div style={{ fontSize: 17, fontWeight: 900, color: B_STRONG }}>
                {parseFloat(avgGain) >= 0 ? "+" : ""}{avgGain} pts
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── R&W + Math dual line ─────────────────────────────────────────────────────
function RWMathTrendChart({ attemptStats }) {
  const [hovIdx, setHovIdx] = useState(null);
  const svgRef = useRef(null);
  const n = attemptStats.length;
  const W = 460, H = 180, padL = 44, padR = 16, padT = 22, padB = 36;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const allSc = [...attemptStats.map((a) => a.rwScore), ...attemptStats.map((a) => a.mathScore)].filter((v) => v != null);
  if (allSc.length === 0) return null;
  const rawMin = Math.min(...allSc), rawMax = Math.max(...allSc);
  const yMin = Math.floor(rawMin / 50) * 50 - 30, yMax = Math.ceil(rawMax / 50) * 50 + 20;
  const yRange = yMax - yMin;
  const xPos = (i) => padL + (i / Math.max(n - 1, 1)) * innerW;
  const yPos = (v) => padT + innerH * (1 - (v - yMin) / yRange);
  const yTicks = [];
  for (let v = Math.ceil(yMin / 50) * 50; v <= yMax; v += 50) yTicks.push(v);
  const makePts = (vals) => vals.map((v, i) => v != null ? `${xPos(i)},${yPos(v)}` : null).filter(Boolean).join(" ");
  const onMove = (e) => {
    const rect = svgRef.current?.getBoundingClientRect(); if (!rect) return;
    const relX = ((e.clientX - rect.left) / rect.width) * W;
    let minD = Infinity, minI = 0;
    for (let i = 0; i < n; i++) { const d = Math.abs(xPos(i) - relX); if (d < minD) { minD = d; minI = i; } }
    setHovIdx(minI);
  };
  const rwImpr   = attemptStats[0]?.rwScore   != null && attemptStats[n-1]?.rwScore   != null ? attemptStats[n-1].rwScore   - attemptStats[0].rwScore   : null;
  const mathImpr = attemptStats[0]?.mathScore != null && attemptStats[n-1]?.mathScore != null ? attemptStats[n-1].mathScore - attemptStats[0].mathScore : null;

  return (
    <div>
      <div style={{ display: "flex", gap: 18, marginBottom: 10, flexWrap: "wrap" }}>
        {[{ color: B_STRONG, label: "Math Score" }, { color: VIOLET, label: "Reading Score" }].map((s) => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: TEXT_SEC }}>
            <span style={{ width: 16, height: 2.5, background: s.color, display: "inline-block", borderRadius: 999 }} />{s.label}
          </div>
        ))}
      </div>
      <div style={{ position: "relative" }}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`}
          style={{ width: "100%", height: "auto", display: "block", cursor: "crosshair" }}
          onMouseMove={onMove} onMouseLeave={() => setHovIdx(null)}>
          {yTicks.map((v) => (
            <g key={v}>
              <line x1={padL} x2={W - padR} y1={yPos(v)} y2={yPos(v)} stroke="#eef2ff" strokeWidth={1} />
              <text x={padL - 6} y={yPos(v) + 4} textAnchor="end" style={{ fontSize: 9, fill: TEXT_MUT }}>{v}</text>
            </g>
          ))}
          {attemptStats.map((_, i) => (
            <text key={i} x={xPos(i)} y={H - 6} textAnchor="middle" style={{ fontSize: 9, fill: TEXT_SEC }}>T{i + 1}</text>
          ))}
          {hovIdx != null && (
            <line x1={xPos(hovIdx)} x2={xPos(hovIdx)} y1={padT} y2={padT + innerH}
              stroke="#bfdbfe" strokeWidth={1.5} strokeDasharray="4 3" />
          )}
          <polyline points={makePts(attemptStats.map((a) => a.mathScore))} fill="none" stroke={B_STRONG} strokeWidth={2} strokeLinejoin="round" />
          <polyline points={makePts(attemptStats.map((a) => a.rwScore))}   fill="none" stroke={VIOLET}   strokeWidth={2} strokeLinejoin="round" />
          {attemptStats.map((a, i) => {
            const isHov = hovIdx === i;
            return (
              <g key={i}>
                {a.mathScore != null && (
                  <g>
                    {n <= 6 && <text x={xPos(i)} y={yPos(a.mathScore) - 8} textAnchor="middle" style={{ fontSize: 9, fontWeight: 700, fill: B_STRONG }}>{a.mathScore}</text>}
                    <circle cx={xPos(i)} cy={yPos(a.mathScore)} r={isHov ? 5 : 3} fill={B_STRONG} stroke="#fff" strokeWidth={1.5} />
                  </g>
                )}
                {a.rwScore != null && (
                  <g>
                    {n <= 6 && <text x={xPos(i)} y={yPos(a.rwScore) + 18} textAnchor="middle" style={{ fontSize: 9, fontWeight: 700, fill: VIOLET }}>{a.rwScore}</text>}
                    <circle cx={xPos(i)} cy={yPos(a.rwScore)} r={isHov ? 5 : 3} fill={VIOLET} stroke="#fff" strokeWidth={1.5} />
                  </g>
                )}
              </g>
            );
          })}
        </svg>
        {hovIdx != null && (
          <div style={{
            position: "absolute", top: 4, left: "50%", transform: "translateX(-50%)",
            background: "#0f172a", color: "#f8fafc", borderRadius: 8, padding: "8px 12px",
            fontSize: 11, pointerEvents: "none", zIndex: 10, whiteSpace: "nowrap",
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          }}>
            <strong>Test {hovIdx + 1}</strong> · {attemptStats[hovIdx]?.date}
            <br />R&amp;W: <strong style={{ color: "#c4b5fd" }}>{attemptStats[hovIdx]?.rwScore ?? "—"}</strong>{" · "}
            Math: <strong style={{ color: "#93c5fd" }}>{attemptStats[hovIdx]?.mathScore ?? "—"}</strong>
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        {mathImpr != null && (
          <div style={{ flex: 1, background: mathImpr >= 0 ? POS_BG : NEG_BG, borderRadius: 10, padding: "9px 12px", border: `1px solid ${mathImpr >= 0 ? "#bfdbfe" : "#fde68a"}` }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: TEXT_MUT, marginBottom: 3 }}>Math Improvement</div>
            <div style={{ fontSize: 17, fontWeight: 900, color: mathImpr >= 0 ? POS_TEXT : NEG_TEXT }}>{mathImpr >= 0 ? "+" : ""}{mathImpr} pts</div>
          </div>
        )}
        {rwImpr != null && (
          <div style={{ flex: 1, background: rwImpr >= 0 ? "#f5f3ff" : NEG_BG, borderRadius: 10, padding: "9px 12px", border: `1px solid ${rwImpr >= 0 ? "#ddd6fe" : "#fde68a"}` }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: TEXT_MUT, marginBottom: 3 }}>Reading Improvement</div>
            <div style={{ fontSize: 17, fontWeight: 900, color: rwImpr >= 0 ? VIOLET : NEG_TEXT }}>{rwImpr >= 0 ? "+" : ""}{rwImpr} pts</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Short axis labels for grouped bar chart ──────────────────────────────────
const CAT_LABELS = {
  "Information and Ideas":             ["Info &", "Ideas"],
  "Craft and Structure":               ["Craft &", "Structure"],
  "Expression of Ideas":               ["Expression", "of Ideas"],
  "Standard English Conventions":      ["Std. English", "Conv."],
  "Algebra":                           ["Algebra"],
  "Advanced Math":                     ["Advanced", "Math"],
  "Problem-Solving and Data Analysis": ["Problem-Solving", "& Data"],
  "Geometry and Trigonometry":         ["Geometry &", "Trig."],
};

// ─── Grouped bar chart: one subject (R&W or Math) ────────────────────────────
function SubjectBarChart({ subject, units, statsA, statsB, labelA, labelB, hovGroup, setHovGroup }) {
  const isRW   = subject === "Reading & Writing";
  const colorA = isRW ? "#ddd6fe" : B_LIGHT;   // light purple / light blue
  const colorB = isRW ? VIOLET    : B_STRONG;   // deep purple / deep blue
  const labelColorA = isRW ? "#7c3aed" : "#1d4ed8";
  const labelColorB = isRW ? "#a78bfa" : "#93c5fd"; // softer version above strong-color bar

  const cats = Object.entries(units).map(([name, subs]) => ({
    name,
    pctA: computeUnitPct(subs, statsA?.subStats),
    pctB: computeUnitPct(subs, statsB?.subStats),
  }));

  const W = 400, H = 220;
  const padL = 18, padR = 8, padT = 28, padB = 50;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const numG   = cats.length;
  const groupW = innerW / numG;
  const barW   = Math.min(groupW * 0.3, 36);
  const gap    = 6;
  const baseY  = padT + innerH;
  const yPos   = (p) => padT + innerH * (1 - p / 100);
  const bH     = (p) => p != null ? Math.max((p / 100) * innerH, 2) : 0;

  return (
    <div>
      {/* Subject heading */}
      <div style={{
        fontSize: 11, fontWeight: 800, textTransform: "uppercase",
        letterSpacing: "0.08em", color: colorB,
        paddingBottom: 6, marginBottom: 8,
        borderBottom: `2px solid ${isRW ? "#ede9fe" : "#dbeafe"}`,
      }}>
        {subject}
      </div>

      {/* Per-chart legend */}
      <div style={{ display: "flex", gap: 14, marginBottom: 6 }}>
        {[{ color: colorA, label: labelA }, { color: colorB, label: labelB }].map((item) => (
          <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: TEXT_SEC }}>
            <span style={{ width: 14, height: 10, background: item.color, borderRadius: 2, display: "inline-block" }} />
            {item.label}
          </div>
        ))}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {/* Y-axis grid */}
        {[0, 25, 50, 75, 100].map((v) => (
          <g key={v}>
            <line x1={padL} x2={W - padR} y1={yPos(v)} y2={yPos(v)}
              stroke={v === 0 ? "#d1d5db" : "#e5e7eb"}
              strokeWidth={v === 0 ? 1.5 : 1}
              strokeDasharray={v === 0 ? undefined : "3 2"} />
            <text x={padL - 3} y={yPos(v) + 3} textAnchor="end"
              style={{ fontSize: 8, fill: TEXT_MUT }}>{v}%</text>
          </g>
        ))}

        {/* Bar groups */}
        {cats.map(({ name, pctA, pctB }, gi) => {
          const gCX   = padL + gi * groupW + groupW / 2;
          const barAX = gCX - barW - gap / 2;
          const barBX = gCX + gap / 2;
          const hA    = bH(pctA), hB = bH(pctB);
          const hovKey = `${subject}-${gi}`;
          const isHov  = hovGroup === hovKey;
          const delta  = pctA != null && pctB != null ? pctB - pctA : null;
          const lines  = CAT_LABELS[name] || [name];

          return (
            <g key={name}
              onMouseEnter={() => setHovGroup(hovKey)}
              onMouseLeave={() => setHovGroup(null)}>

              {/* Hover highlight background */}
              {isHov && (
                <rect x={gCX - groupW / 2 + 2} y={padT - 4}
                  width={groupW - 4} height={innerH + 8}
                  fill={isRW ? "#faf5ff" : "#eff6ff"} rx={4} />
              )}

              {/* Bar A (earlier test — light color) */}
              {pctA != null && (
                <g>
                  <rect x={barAX} y={yPos(pctA)} width={barW} height={hA}
                    fill={colorA} rx={3} opacity={isHov ? 1 : 0.9} />
                  <text x={barAX + barW / 2} y={yPos(pctA) - 3} textAnchor="middle"
                    style={{ fontSize: 8, fontWeight: 700, fill: labelColorA }}>{pctA}%</text>
                </g>
              )}

              {/* Bar B (later test — strong color) */}
              {pctB != null && (
                <g>
                  <rect x={barBX} y={yPos(pctB)} width={barW} height={hB}
                    fill={colorB} rx={3} opacity={isHov ? 1 : 0.9} />
                  <text x={barBX + barW / 2} y={yPos(pctB) - 3} textAnchor="middle"
                    style={{ fontSize: 8, fontWeight: 700, fill: labelColorB }}>{pctB}%</text>
                </g>
              )}

              {/* Delta row below baseline */}
              {delta != null && (
                <text x={gCX} y={baseY + 11} textAnchor="middle" style={{
                  fontSize: 9, fontWeight: 700,
                  fill: delta > 0 ? POS_TEXT : delta < 0 ? NEG_TEXT : TEXT_MUT,
                }}>
                  {delta > 0 ? `▲ +${delta}%` : delta < 0 ? `▼ ${delta}%` : "—"}
                </text>
              )}

              {/* Category label (up to 2 lines) */}
              {lines.map((line, li) => (
                <text key={li} x={gCX} y={baseY + 22 + li * 11} textAnchor="middle"
                  style={{ fontSize: 9, fill: TEXT_SEC }}>{line}</text>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Full-width category skill breakdown ──────────────────────────────────────
// Grouped bar charts, full category names, R&W left | Math right
function FullWidthCategorySkill({ statsA, statsB, labelA, labelB }) {
  const [hovGroup, setHovGroup] = useState(null);

  return (
    <div style={{ display: "flex", gap: 28 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <SubjectBarChart
          subject="Reading & Writing" units={SAT_STRUCTURE["Reading & Writing"]}
          statsA={statsA} statsB={statsB} labelA={labelA} labelB={labelB}
          hovGroup={hovGroup} setHovGroup={setHovGroup} />
      </div>
      <div style={{ width: 1, background: "linear-gradient(180deg, #ddd6fe, #bfdbfe)", flexShrink: 0, opacity: 0.8 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <SubjectBarChart
          subject="Math" units={SAT_STRUCTURE["Math"]}
          statsA={statsA} statsB={statsB} labelA={labelA} labelB={labelB}
          hovGroup={hovGroup} setHovGroup={setHovGroup} />
      </div>
    </div>
  );
}

// ─── Subject column — neutral gray/white, matching Individual Report ──────────
function SubjectColumn({ subject, units, statsA, statsB, labelA, labelB }) {
  return (
    <div style={{
      background: CARD_BG,
      borderRadius: 12,
      border: `1px solid ${BORDER}`,
      overflow: "hidden",
    }}>
      {/* Subject header — neutral gray, same as Individual Report */}
      <div style={{
        padding: "10px 14px",
        borderBottom: `1px solid ${BORDER}`,
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: TEXT_PRI,
        background: "#f3f4f6",
      }}>
        {subject}
      </div>

      {/* Column headers */}
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "52%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "16%" }} />
        </colgroup>
        <thead>
          <tr>
            {[["Question Type", "left"], [labelA, "right"], [labelB, "right"], ["Change", "right"]].map(([h, align]) => (
              <th key={h} style={{
                fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em",
                textAlign: align, color: TEXT_SEC, padding: "7px 10px",
                borderBottom: `1px solid ${BORDER}`, background: "#f9fafb",
                whiteSpace: "nowrap",
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(units).map(([unitName, subs]) => {
            const anyData = subs.some((sub) => {
              const k = normalize(sub);
              return (statsA?.subStats?.[k]?.total || 0) > 0 || (statsB?.subStats?.[k]?.total || 0) > 0;
            });
            if (!anyData) return null;

            const unitPctA = computeUnitPct(subs, statsA?.subStats);
            const unitPctB = computeUnitPct(subs, statsB?.subStats);
            const unitDelta = unitPctA != null && unitPctB != null ? unitPctB - unitPctA : null;

            return (
              <React.Fragment key={unitName}>
                {/* Category row — neutral gray, matches Individual Report unitRow */}
                <tr style={{ background: "#e5e7eb", fontWeight: 700, fontSize: 13 }}>
                  <td style={{ padding: "8px 10px", borderBottom: `1px solid #d1d5db`, color: TEXT_PRI }}>
                    {unitName}
                  </td>
                  <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: `1px solid #d1d5db`, fontSize: 12, color: TEXT_SEC }}>
                    {unitPctA != null ? `${unitPctA}%` : <span style={{ color: "#d1d5db" }}>—</span>}
                  </td>
                  <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: `1px solid #d1d5db` }}>
                    {unitPctB != null ? <AccBadge percentage={unitPctB} /> : <span style={{ color: "#d1d5db" }}>—</span>}
                  </td>
                  <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: `1px solid #d1d5db` }}>
                    {unitDelta != null && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: unitDelta > 0 ? POS_TEXT : unitDelta < 0 ? NEG_TEXT : TEXT_MUT }}>
                        {unitDelta > 0 ? "▲ +" : unitDelta < 0 ? "▼ " : "—"}{unitDelta !== 0 ? `${unitDelta}%` : ""}
                      </span>
                    )}
                  </td>
                </tr>

                {/* Subcategory rows */}
                {subs.map((sub) => {
                  const k = normalize(sub);
                  const aD = statsA?.subStats?.[k], bD = statsB?.subStats?.[k];
                  const aPct = aD ? pct(aD.correct, aD.total) : null;
                  const bPct = bD ? pct(bD.correct, bD.total) : null;
                  if (aPct == null && bPct == null) return null;
                  const delta = aPct != null && bPct != null ? bPct - aPct : null;
                  return (
                    <tr key={sub} style={{ background: CARD_BG }}>
                      <td style={{ padding: "6px 10px 6px 20px", fontSize: 12, color: TEXT_PRI, borderBottom: `1px solid #f3f4f6` }}>
                        {sub}
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 12, color: TEXT_SEC, borderBottom: `1px solid #f3f4f6` }}>
                        {aPct != null ? `${aPct}%` : <span style={{ color: "#d1d5db" }}>—</span>}
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "right", borderBottom: `1px solid #f3f4f6` }}>
                        {bPct != null ? <AccBadge percentage={bPct} /> : <span style={{ color: "#d1d5db" }}>—</span>}
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "right", borderBottom: `1px solid #f3f4f6` }}>
                        {delta == null
                          ? <span style={{ fontSize: 11, color: "#d1d5db" }}>—</span>
                          : delta === 0
                          ? <span style={{ fontSize: 11, color: TEXT_MUT }}>same</span>
                          : <span style={{ fontSize: 11, fontWeight: 600, color: delta > 0 ? POS_TEXT : NEG_TEXT }}>
                              {delta > 0 ? "▲ +" : "▼ "}{delta}%
                            </span>}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Chart card ───────────────────────────────────────────────────────────────
function ChartCard({ number, title, subtitle, children }) {
  return (
    <div style={{
      background: CARD_BG, borderRadius: 14, border: `1px solid ${BORDER}`, padding: "16px 18px",
      boxShadow: "0 4px 16px rgba(15,23,42,0.05)",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
        <span style={{
          width: 24, height: 24, borderRadius: "50%",
          background: B_GRAD, color: "#1d4ed8", fontSize: 11, fontWeight: 800,
          border: "1px solid #bfdbfe",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>{number}</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: TEXT_PRI }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: TEXT_SEC, marginTop: 2 }}>{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DiagnosticProgressReport({
  attempts,
  diagnosticCollection = "sat_diagnostic_tests",
}) {
  const [qIndexByTestId, setQIndexByTestId] = useState({});
  const [loading, setLoading] = useState(true);
  const [compareMode, setCompareMode] = useState("auto");
  const [customA, setCustomA] = useState(0);
  const [customB, setCustomB] = useState(1);

  const sorted = useMemo(
    () => [...(attempts || [])].sort((a, b) => (toDateSafe(a?.completedAt)?.getTime() || 0) - (toDateSafe(b?.completedAt)?.getTime() || 0)),
    [attempts]
  );
  const uniqueTestIds = useMemo(() => [...new Set(sorted.map((a) => a?.testId).filter(Boolean))], [sorted]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const result = {};
      await Promise.all(uniqueTestIds.map(async (tid) => {
        try {
          const snap = await getDocs(collection(db, diagnosticCollection, tid, "questions"));
          const map = new Map();
          snap.forEach((d) => { const v = d.data() || {}; map.set(v.questionId || d.id, v); });
          result[tid] = map;
        } catch (e) {
          console.warn("DiagnosticProgressReport: failed testId", tid, e);
          result[tid] = new Map();
        }
      }));
      if (!cancelled) { setQIndexByTestId(result); setLoading(false); }
    }
    if (uniqueTestIds.length === 0) { setLoading(false); return; }
    load();
    return () => { cancelled = true; };
  }, [uniqueTestIds, diagnosticCollection]);

  const attemptStats = useMemo(
    () => sorted.map((attempt, idx) => {
      const qIndex = qIndexByTestId[attempt?.testId] || new Map();
      const computed = computeAttemptStats(attempt, qIndex);
      const dateObj = toDateSafe(attempt?.completedAt);
      const date = dateObj ? dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
      return {
        idx, attemptId: attempt.attemptId,
        label: `Test ${idx + 1}`, date,
        totalScore: resolveScore(attempt),
        rwScore: resolveRW(attempt),
        mathScore: resolveMath(attempt),
        ...computed,
      };
    }),
    [sorted, qIndexByTestId]
  );

  const n = attemptStats.length;
  const latestIdx = n - 1;
  const lowestIdx = useMemo(() => {
    let minScore = Infinity, minIdx = 0;
    attemptStats.forEach((a, i) => { if (a.totalScore != null && a.totalScore < minScore) { minScore = a.totalScore; minIdx = i; } });
    return minIdx;
  }, [attemptStats]);

  const { idxA, idxB } = useMemo(() => {
    if (n < 2) return { idxA: 0, idxB: 0 };
    if (compareMode === "custom") {
      const a = Math.max(0, Math.min(n - 1, customA)), b = Math.max(0, Math.min(n - 1, customB));
      if (a === b) return { idxA: 0, idxB: n - 1 };
      return { idxA: Math.min(a, b), idxB: Math.max(a, b) };
    }
    if (compareMode === "prev")  return { idxA: Math.max(0, latestIdx - 1), idxB: latestIdx };
    if (compareMode === "first") return { idxA: 0, idxB: latestIdx };
    if (n === 2) return { idxA: 0, idxB: 1 };
    if (lowestIdx !== latestIdx) return { idxA: lowestIdx, idxB: latestIdx };
    return { idxA: Math.max(0, latestIdx - 1), idxB: latestIdx };
  }, [compareMode, customA, customB, n, latestIdx, lowestIdx]);

  const statsA = attemptStats[idxA];
  const statsB = attemptStats[idxB];
  const statsLatest = attemptStats[latestIdx];

  const totalDelta = statsA?.totalScore != null && statsB?.totalScore != null ? statsB.totalScore - statsA.totalScore : null;
  const rwDelta    = statsA?.rwScore    != null && statsB?.rwScore    != null ? statsB.rwScore    - statsA.rwScore    : null;
  const mathDelta  = statsA?.mathScore  != null && statsB?.mathScore  != null ? statsB.mathScore  - statsA.mathScore  : null;
  const accDelta   = statsA?.accuracy   != null && statsB?.accuracy   != null ? statsB.accuracy   - statsA.accuracy   : null;

  const bestScore = Math.max(...attemptStats.map((a) => a.totalScore ?? -Infinity).filter(isFinite));
  const bestScoreTest = attemptStats.find((a) => a.totalScore === bestScore);
  const isGain = totalDelta == null ? null : totalDelta >= 0;

  const takeaways = useMemo(() => {
    if (!statsA || !statsB) return [];
    const items = [];
    if (totalDelta != null) {
      items.push({
        type: totalDelta > 0 ? "good" : totalDelta < 0 ? "warn" : "neutral",
        text: totalDelta > 0
          ? `Score improved by +${totalDelta} points (${statsA.totalScore} → ${statsB.totalScore}) from ${statsA.label} to ${statsB.label}.`
          : totalDelta < 0
          ? `Score decreased by ${Math.abs(totalDelta)} points (${statsA.totalScore} → ${statsB.totalScore}) from ${statsA.label} to ${statsB.label}.`
          : `Score held steady at ${statsB.totalScore} between ${statsA.label} and ${statsB.label}.`,
      });
    }
    if (rwDelta != null && mathDelta != null && (Math.abs(rwDelta) >= 20 || Math.abs(mathDelta) >= 20)) {
      if (rwDelta > 0 && rwDelta >= mathDelta)
        items.push({ type: "good", text: `Reading & Writing showed the strongest growth (+${rwDelta} pts). Math ${mathDelta >= 0 ? `also improved (+${mathDelta} pts)` : `dropped (${mathDelta} pts)`}.` });
      else if (mathDelta > 0 && mathDelta > rwDelta)
        items.push({ type: "good", text: `Math showed the strongest growth (+${mathDelta} pts). Reading & Writing ${rwDelta >= 0 ? `also improved (+${rwDelta} pts)` : `dropped (${rwDelta} pts)`}.` });
      else if (rwDelta < -19)
        items.push({ type: "warn", text: `Reading & Writing dropped ${rwDelta} pts — needs focused attention before the next test.` });
      else if (mathDelta < -19)
        items.push({ type: "warn", text: `Math dropped ${mathDelta} pts — needs focused attention before the next test.` });
    }
    const improvements = ALL_SUBS.map(({ unit, sub }) => {
      const k = normalize(sub); const aD = statsA.subStats[k]; const bD = statsB.subStats[k];
      if (!aD || !bD) return null;
      const d = pct(bD.correct, bD.total) - pct(aD.correct, aD.total);
      return { sub, unit, delta: d };
    }).filter((x) => x && x.delta != null && x.delta >= 20).sort((a, b) => b.delta - a.delta);
    if (improvements.length > 0) {
      const top = improvements[0];
      items.push({ type: "good", text: `Biggest skill improvement: "${top.sub}" (+${top.delta}%) under ${top.unit}.` });
    }
    const weakest = ALL_SUBS.map(({ unit, sub }) => {
      const k = normalize(sub); const d = statsLatest?.subStats?.[k];
      if (!d || d.total === 0) return null;
      return { sub, unit, pct: pct(d.correct, d.total) };
    }).filter((x) => x && x.pct != null && x.pct < 70).sort((a, b) => a.pct - b.pct);
    if (weakest.length > 0) {
      const w = weakest[0];
      items.push({ type: "warn", text: `Current weakness: "${w.sub}" at ${w.pct}% in the latest test — top tutoring priority.` });
    }
    return items.slice(0, 4);
  }, [statsA, statsB, statsLatest, totalDelta, rwDelta, mathDelta]);

  const focusAreas = useMemo(() => {
    if (!statsLatest) return [];
    return ALL_SUBS.map(({ subject, unit, sub }) => {
      const k = normalize(sub); const d = statsLatest.subStats[k];
      if (!d || d.total === 0) return null;
      const latestPct = pct(d.correct, d.total);
      const aD = statsA?.subStats?.[k];
      const delta = aD && aD.total > 0 ? latestPct - pct(aD.correct, aD.total) : null;
      const score = (latestPct ?? 100) + (delta != null && delta < 0 ? delta * 1.5 : 0);
      return { subject, unit, sub, latestPct, delta, score };
    }).filter((x) => x && x.latestPct != null && x.latestPct < 90).sort((a, b) => a.score - b.score).slice(0, 4);
  }, [statsLatest, statsA]);

  // ─── Guards ───────────────────────────────────────────────────────────────
  if (sorted.length < 2) {
    return (
      <div style={{ padding: "40px 24px", textAlign: "center", color: TEXT_SEC, border: `1px solid ${BORDER}`, borderRadius: 14, background: "#eef2ff" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>📈</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: TEXT_PRI, marginBottom: 8 }}>Progress Comparison Not Yet Available</div>
        <div style={{ fontSize: 13, maxWidth: 340, margin: "0 auto", color: TEXT_SEC }}>
          Progress comparison will appear automatically after a second diagnostic is completed.
        </div>
      </div>
    );
  }

  if (loading) {
    return <div style={{ padding: 24, color: TEXT_SEC, textAlign: "center", fontSize: 13 }}>Loading comparison data…</div>;
  }

  // ─── Shared styles ────────────────────────────────────────────────────────
  const panel = {
    background: "rgba(255,255,255,0.88)",
    borderRadius: 16,
    border: `1px solid rgba(229,231,235,0.9)`,
    padding: "18px 20px",
    marginBottom: 12,
    boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
  };

  const secTitle = {
    fontSize: 14, fontWeight: 800, color: TEXT_PRI, margin: "0 0 14px 0",
  };

  const modeOptions = [
    { key: "auto",   label: n === 2 ? "Test 1 vs Test 2" : "Lowest vs Latest" },
    ...(n >= 3 ? [{ key: "prev",  label: "Previous vs Latest" }] : []),
    ...(n >= 3 ? [{ key: "first", label: "First vs Latest" }] : []),
    ...(n >= 3 ? [{ key: "custom", label: "Choose Tests" }] : []),
  ];

  // Styled select for custom mode (Individual Report badge style)
  const StyledSelect = ({ value, onChange, lbl }) => (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", color: "#334155", marginBottom: 5 }}>{lbl}</div>
      <div style={{ position: "relative", display: "inline-block" }}>
        <select value={value} onChange={(e) => onChange(Number(e.target.value))}
          style={{
            appearance: "none", WebkitAppearance: "none",
            fontSize: 13, fontWeight: 600, padding: "9px 36px 9px 12px",
            borderRadius: 12, border: `1px solid #bfdbfe`,
            background: B_GRAD, color: "#1d4ed8", outline: "none",
            minWidth: 210, cursor: "pointer", boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
          }}>
          {attemptStats.map((a, i) => (
            <option key={i} value={i}>{a.label} · {a.date}{a.totalScore ? ` (${a.totalScore})` : ""}</option>
          ))}
        </select>
        <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#1d4ed8", fontSize: 11, fontWeight: 800 }}>▾</div>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif', color: TEXT_PRI }}>

      {/* ── Comparison mode selector ─────────────────────────────────────────── */}
      {n >= 3 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: "#334155", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 8 }}>Comparing</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {modeOptions.map((opt) => {
              const isActive = compareMode === opt.key;
              return (
                <button key={opt.key} onClick={() => setCompareMode(opt.key)} style={{
                  fontSize: 12, fontWeight: isActive ? 800 : 600, padding: "7px 14px", borderRadius: 999,
                  border: `1px solid ${isActive ? "#bfdbfe" : BORDER}`,
                  background: isActive ? B_GRAD : CARD_BG,
                  color: isActive ? "#1d4ed8" : "#374151",
                  cursor: "pointer", outline: "none", transition: "all 0.12s",
                  boxShadow: isActive ? "0 8px 20px rgba(15,23,42,0.08)" : "none",
                }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
          {compareMode === "custom" && (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 14, marginTop: 10, flexWrap: "wrap" }}>
              <StyledSelect value={customA} onChange={setCustomA} lbl="Starting Test" />
              <span style={{ color: TEXT_MUT, fontSize: 13, fontWeight: 600, paddingBottom: 10 }}>vs</span>
              <StyledSelect value={customB} onChange={setCustomB} lbl="Ending Test" />
            </div>
          )}
        </div>
      )}

      {/* ── Score Comparison Hero ─────────────────────────────────────────────── */}
      <div style={{ ...panel, background: "linear-gradient(145deg, #ffffff, #eff6ff)", border: `1px solid #dbeafe` }}>
        <h4 style={secTitle}>Score Comparison</h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.7fr 1fr", gap: 24, alignItems: "center" }}>

          {/* Test A text */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: TEXT_MUT, marginBottom: 3 }}>{statsA?.label}</div>
            <div style={{ fontSize: 11, color: TEXT_SEC, marginBottom: 10 }}>{statsA?.date}</div>
            <div style={{ fontSize: 38, fontWeight: 900, color: "#374151", lineHeight: 1, marginBottom: 12, letterSpacing: "-0.03em" }}>
              {statsA?.totalScore ?? "—"}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ fontSize: 12, color: TEXT_SEC }}>R&amp;W <strong style={{ color: TEXT_PRI }}>{statsA?.rwScore ?? "—"}</strong></div>
              <div style={{ fontSize: 12, color: TEXT_SEC }}>Math <strong style={{ color: TEXT_PRI }}>{statsA?.mathScore ?? "—"}</strong></div>
              {statsA?.accuracy != null && <div style={{ fontSize: 12, color: TEXT_SEC }}>Accuracy <strong style={{ color: TEXT_PRI }}>{statsA.accuracy}%</strong></div>}
            </div>
          </div>

          {/* Center: stacked bar chart */}
          <StackedBarComparison
            statsA={statsA} statsB={statsB}
            labelA={statsA?.label || ""} labelB={statsB?.label || ""}
            totalDelta={totalDelta}
          />

          {/* Test B text */}
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: TEXT_MUT, marginBottom: 3 }}>{statsB?.label}</div>
            <div style={{ fontSize: 11, color: TEXT_SEC, marginBottom: 10 }}>{statsB?.date}</div>
            <div style={{ fontSize: 38, fontWeight: 900, color: isGain === false ? NEG_TEXT : B_STRONG, lineHeight: 1, marginBottom: 12, letterSpacing: "-0.03em" }}>
              {statsB?.totalScore ?? "—"}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-end" }}>
              <div style={{ fontSize: 12, color: TEXT_SEC, display: "flex", gap: 7, alignItems: "center" }}>
                R&amp;W <strong style={{ color: TEXT_PRI }}>{statsB?.rwScore ?? "—"}</strong>
                {rwDelta != null && <DeltaTag delta={rwDelta} />}
              </div>
              <div style={{ fontSize: 12, color: TEXT_SEC, display: "flex", gap: 7, alignItems: "center" }}>
                Math <strong style={{ color: TEXT_PRI }}>{statsB?.mathScore ?? "—"}</strong>
                {mathDelta != null && <DeltaTag delta={mathDelta} />}
              </div>
              {statsB?.accuracy != null && (
                <div style={{ fontSize: 12, color: TEXT_SEC, display: "flex", gap: 7, alignItems: "center" }}>
                  Accuracy <strong>{statsB.accuracy}%</strong>
                  {accDelta != null && <DeltaTag delta={accDelta} suffix="%" />}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick stat tiles (matches Individual Report tile style) */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${BORDER}` }}>
          {[
            { label: "Best Score", value: isFinite(bestScore) ? bestScore : "—", sub: bestScoreTest?.label || "" },
            { label: "Tests Completed", value: n, sub: "total attempts" },
            { label: "Latest Accuracy", value: statsLatest?.accuracy != null ? `${statsLatest.accuracy}%` : "—", sub: "overall correct rate" },
          ].map((item) => (
            <div key={item.label} style={{ padding: "10px 12px", borderRadius: 14, background: TILE_BG, border: `1px solid ${BORDER}` }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: TEXT_SEC, marginBottom: 4 }}>{item.label}</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#1d4ed8" }}>{item.value}</div>
              {item.sub && <div style={{ fontSize: 10, color: TEXT_SEC, marginTop: 1 }}>{item.sub}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* ── Difficulty cards (3) ──────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 12 }}>
        {["Easy", "Medium", "Hard"].map((diff) => {
          const dc = { Easy: "#15803d", Medium: "#d97706", Hard: "#b91c1c" }[diff];
          const aBkt = statsA?.diffBuckets?.[diff], bBkt = statsB?.diffBuckets?.[diff];
          const aPct = aBkt ? pct(aBkt.correct, aBkt.total) : null;
          const bPct = bBkt ? pct(bBkt.correct, bBkt.total) : null;
          const delta = aPct != null && bPct != null ? bPct - aPct : null;
          return (
            <div key={diff} style={{ background: "rgba(255,255,255,0.88)", borderRadius: 14, border: `1px solid ${BORDER}`, padding: "14px 12px", boxShadow: "0 4px 12px rgba(15,23,42,0.04)" }}>
              <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: dc, marginBottom: 8 }}>{diff}</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 10,
                  alignItems: "start",
                }}
              >
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <DonutChart
                    value={aPct}
                    max={100}
                    size={92}
                    strokeWidth={8}
                    label={statsA?.label || ""}
                    valueFormatter={(v) => `${Math.round(v)}%`}
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <DonutChart
                    value={bPct}
                    max={100}
                    size={92}
                    strokeWidth={8}
                    label={statsB?.label || ""}
                    valueFormatter={(v) => `${Math.round(v)}%`}
                  />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: TEXT_MUT, marginTop: 2 }}>
                <span>{aBkt && aBkt.total > 0 ? `${aBkt.correct}/${aBkt.total}` : "—"}</span>
                <span>{bBkt && bBkt.total > 0 ? `${bBkt.correct}/${bBkt.total}` : "—"}</span>
              </div>
              {delta != null && (
                <div style={{ marginTop: 10, display: "flex", justifyContent: "center" }}>
                  <DeltaTag delta={delta} suffix="%" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Chart row: Score Trend + R&W Math ────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <ChartCard number="1" title="Score Trend Line" subtitle="Overall score improvement over all tests">
          <ScoreTrendChart attemptStats={attemptStats} />
        </ChartCard>
        <ChartCard number="2" title="Reading & Math Dual Line" subtitle="Compare section scores over time">
          <RWMathTrendChart attemptStats={attemptStats} />
        </ChartCard>
      </div>

      {/* ── Full-width Skill Breakdown ────────────────────────────────────────── */}
      <div style={{ ...panel, background: "linear-gradient(145deg, #ffffff, #f0f7ff)", border: `1px solid #dbeafe` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <h4 style={{ ...secTitle, fontSize: 15 }}>Skill Breakdown — Main Categories</h4>
          <div style={{
            fontSize: 11, color: POS_TEXT, fontWeight: 700,
            background: POS_BG, padding: "4px 12px", borderRadius: 999,
            border: "1px solid #bfdbfe",
          }}>{statsA?.label} vs {statsB?.label}</div>
        </div>
        <FullWidthCategorySkill statsA={statsA} statsB={statsB} labelA={statsA?.label || "Start"} labelB={statsB?.label || "Latest"} />
      </div>

      {/* ── Subcategory breakdown ─────────────────────────────────────────────── */}
      <div style={{
        background: "#f9fafb",
        borderRadius: 16,
        padding: 16,
        border: `1px solid ${BORDER}`,
        marginBottom: 12,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: TEXT_PRI, margin: 0 }}>Subcategory Breakdown</h3>
          <div style={{ fontSize: 11, color: TEXT_SEC }}>{statsA?.label} → {statsB?.label} · skills with data only</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
          {Object.entries(SAT_STRUCTURE).map(([subject, units]) => (
            <SubjectColumn key={subject} subject={subject} units={units}
              statsA={statsA} statsB={statsB}
              labelA={statsA?.label || "Start"} labelB={statsB?.label || "Latest"}
            />
          ))}
        </div>
      </div>

      {/* ── Key Takeaways ─────────────────────────────────────────────────────── */}
      {takeaways.length > 0 && (
        <div style={panel}>
          <h4 style={secTitle}>Key Takeaways</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {takeaways.map((t, i) => {
              const isGoodT = t.type === "good", isWarnT = t.type === "warn";
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px", borderRadius: 10,
                  background: isGoodT ? POS_BG : isWarnT ? "#fef3c7" : "#f1f5f9",
                  border: `1px solid ${isGoodT ? "#bfdbfe" : isWarnT ? "#fde68a" : BORDER}`,
                }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: "50%", flexShrink: 0, marginTop: 1,
                    background: isGoodT ? B_STRONG : isWarnT ? "#d97706" : TEXT_SEC,
                    color: "#fff", fontSize: 11, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isGoodT ? "✓" : isWarnT ? "!" : "→"}
                  </span>
                  <span style={{ fontSize: 13, color: TEXT_PRI, lineHeight: 1.65 }}>{t.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Recommended Focus ─────────────────────────────────────────────────── */}
      {focusAreas.length > 0 && (
        <div style={panel}>
          <h4 style={secTitle}>Recommended Focus — Next Tutoring Priorities</h4>
          <div>
            {focusAreas.map((r, i) => {
              const isCritical = i < 2 || (r.latestPct != null && r.latestPct < 55);
              const priorityLabel = isCritical ? "Critical" : "High";
              const pColor = isCritical ? "#b91c1c" : "#d97706";
              const pBg    = isCritical ? "#fee2e2" : "#fef3c7";
              return (
                <div key={r.sub} style={{
                  display: "grid", gridTemplateColumns: "28px 1fr auto auto",
                  alignItems: "center", gap: 14, padding: "13px 0",
                  borderBottom: i < focusAreas.length - 1 ? `1px solid #f3f4f6` : "none",
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    background: "#eef2ff", color: "#1d4ed8", fontSize: 11, fontWeight: 800,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>{i + 1}</div>
                  <div>
                    <div style={{ fontSize: 10, color: TEXT_MUT, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>{r.unit}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_PRI }}>{r.sub}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, color: TEXT_MUT, marginBottom: 2 }}>Latest</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: r.latestPct < 55 ? "#b91c1c" : r.latestPct < 75 ? "#d97706" : B_STRONG }}>
                      {r.latestPct}%
                    </div>
                    {r.delta != null && r.delta !== 0 && <DeltaTag delta={r.delta} suffix="%" />}
                  </div>
                  <div>
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: "4px 10px", borderRadius: 999,
                      background: pBg, color: pColor,
                      textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap",
                      border: `1px solid ${pColor}33`,
                    }}>
                      {priorityLabel}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: 11, color: TEXT_MUT, marginTop: 14, marginBottom: 0, lineHeight: 1.6 }}>
            Priorities ranked by lowest current performance and declining trends. Your tutor's Rx hours plan takes precedence.
          </p>
        </div>
      )}
    </div>
  );
}
